import _ from 'lodash';
import {Decimal} from 'decimal.js';
import * as orderTypes from '../../../types';
import * as returnTypes from './types';
import * as notify from '../../../../lang/en/notify';
import {
  extractD365Item,
  extractProductDetails,
  percentOf,
  sumTaxLines,
} from '../../../../utils';
import {RepositoryFactory} from '../../../../repositories/RepositoryFactory';
import router from '../../../../router';

const D365OrderRepository = RepositoryFactory.get('acadia/d365/order');
const D365RARepository = RepositoryFactory.get('acadia/d365/return');
const ShopifyOrderRepository = RepositoryFactory.get(
    'acadia/shopify/orders/order');
const ShopifyOrderRefundRepository = RepositoryFactory.get(
    'acadia/shopify/orders/refund');
const ShopifyPriceRuleRepository = RepositoryFactory.get(
    'acadia/shopify/discounts/price-rule');
const ShopifyProductRepository = RepositoryFactory.get(
    'acadia/shopify/products/product');
const draftRAInitial = {
  subtotal_price: 0,
  total_tax: 0,
  total_price: 0,
  tax_lines: [],
};
const shippingInitial = {
  custom: true,
  title: 'Custom Shipping',
  price: 0,
  max_price: 0,
};
const discountInitial = {
  selected: 'Code',
  value: '',
  amount: '',
  price_rule: null,
};
const returnAuthorizationInitial = {
  'id': 0,
  'region': '',
  'invoice_id': 0,
  'return_status': '',
  'return_reason': '',
  'warehouse_code': '',
  'note': '',
  'misc': '0.00',
  'gl_code': '',
  'freight': '0.00',
  'currency': '',
  'tax': '0.00',
  'tax_code': '',
  'items': [],
};
const totalLineItemsPriceReducer = (total, product) => {
  const price = Number(product.returnItem.price);
  const quantity = Number(product.returnItem.quantity);
  return percentOf(total + price * quantity, 1);
};

const getDefaultState = () => ({
  state: null,
  loading: false,
  id: null,
  region: '',
  order: null,
  draftRA: {...draftRAInitial},
  draftOrder: null,
  draftRefund: null,
  returnAuthorization: {...returnAuthorizationInitial},
  d365Order: null,
  d365Error: '',
  raRemarks: '',
  textRemarks: '',
  reasonCode: '',
  reasonCodeDetail: '',
  products: [],
  discount: {...discountInitial},
  shipping: {...shippingInitial},
  note: '',
  warehouse: false,
  originalDiscount: null,
  notifyRefund: true,
  hideButton: false,
});

export default {
  state: getDefaultState(),
  mutations: {
    setOriginalDiscount(state, originalDiscount) {
      state.originalDiscount = originalDiscount;
    },
    [returnTypes.ORDER_RETURN_SET_DEFAULT](state) {
      Object.assign(state, getDefaultState());
    },
    [returnTypes.ORDER_RETURN_SET_NOTIFY](state, notifyRefund) {
      state.notifyRefund = notifyRefund;
    },
    [returnTypes.ORDER_RETURN_SET_STATE](state, transitionTo) {
      state.state = transitionTo;
    },
    [returnTypes.ORDER_RETURN_SET_ID](state, id) {
      state.id = id;
    },
    [returnTypes.ORDER_RETURN_SET_AUTHORIZATION](state, returnAuthorization) {
      state.returnAuthorization = returnAuthorization;
    },
    [returnTypes.ORDER_RETURN_SET_DRAFT_RA](state, draftRA) {
      state.draftRA = {...state.draftRA, ...draftRA};
    },
    [returnTypes.ORDER_RETURN_SET_DRAFT_ORDER](state, draftOrder) {
      state.draftOrder = draftOrder;
    },
    [returnTypes.ORDER_RETURN_SET_DRAFT_REFUND](state, draftRefund) {
      state.draftRefund = draftRefund;
    },
    [returnTypes.ORDER_RETURN_SET_DISCOUNT](state, discount) {
      state.discount = discount;
    },
    [returnTypes.ORDER_RETURN_SET_LOADING](state, loading) {
      state.loading = loading;
    },
    [returnTypes.ORDER_RETURN_SET_LOGON](state, logOnOrder) {
      state.logOnOrder = logOnOrder;
    },
    [returnTypes.ORDER_RETURN_SET_LOGON_ERROR](state, logOnError) {
      state.logOnError = logOnError;
    },
    [returnTypes.ORDER_RETURN_SET_D365](state, d365Order) {
      state.d365Order = d365Order;
    },
    [returnTypes.ORDER_RETURN_SET_D365_ERROR](state, d365Error) {
      state.d365Error = d365Error;
    },
    [returnTypes.ORDER_RETURN_SET_NOTE](state, note) {
      state.note = note;
    },
    [returnTypes.ORDER_RETURN_SET_ORDER](state, order) {
      state.order = order;
    },
    [returnTypes.ORDER_RETURN_SET_PRODUCTS](state, products) {
      state.products = products;
    },
    [returnTypes.ORDER_RETURN_SET_PRODUCT_ADD](state, product) {
      state.products.push(product);
    },
    [returnTypes.ORDER_RETURN_SET_PRODUCT_UPDATE](state, {index, product}) {
      state.products.splice(index, 1, product);
    },
    [returnTypes.ORDER_RETURN_SET_REASON_CODE](state, reasonCode) {
      state.reasonCode = reasonCode;
    },
    [returnTypes.ORDER_RETURN_SET_REASON_CODE_DETAIL](state, reasonCodeDetail) {
      state.reasonCodeDetail = reasonCodeDetail;
    },
    [returnTypes.ORDER_RETURN_SET_REGION](state, region) {
      state.region = region;
    },
    [returnTypes.ORDER_RETURN_SET_REMARKS](state, raRemarks) {
      state.raRemarks = raRemarks;
    },
    [returnTypes.ORDER_RETURN_SET_TEXT_REMARKS](state, textRemarks) {
      state.textRemarks = textRemarks;
    },
    [returnTypes.ORDER_RETURN_SET_SHIPPING](state, shipping) {
      state.shipping = shipping;
    },
    [returnTypes.ORDER_RETURN_SET_WAREHOUSE](state, warehouse) {
      state.warehouse = warehouse;
    },
    [returnTypes.ORDER_RETURN_SET_HIDE_BUTTON](state, status = false) {
      state.hideButton = status;
    },
  },
  actions: {
    [returnTypes.ORDER_RETURN_RESET]({commit}) {
      commit(returnTypes.ORDER_RETURN_SET_DEFAULT);
    },
    [returnTypes.ORDER_RETURN_CREATE_RA]({state, getters, dispatch, commit}) {
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);
      const {region, note, order} = state;
      const {id: orderId} = order;
      const payload = getters[returnTypes.ORDER_RETURN_GET_AUTHORIZATION];

      //Compare totals between shopify refund and d365
      const itemTotal = _.reduce(payload.items, function(sum, i) {
        return sum + (parseFloat(i.unit_price) * i.quantity);
      }, 0);
      const d365Total = (payload.discount > 0 ? payload.discount + payload.tax +
          payload.freight : itemTotal + payload.tax + payload.freight).toFixed(
          2);
      const shopifyTotal = _.reduce(state.draftRefund.transactions,
          function(sum, t) {
            return sum + parseFloat(t.amount);
          }, 0).toFixed(2);
      const difference = new Decimal(d365Total - shopifyTotal);
      const quantity = _.reduce(payload.items, function(sum, i) {
        return sum + i.quantity;
      }, 0);
      if (region !== 'amazon_usa' &&
          Math.abs(difference.toDecimalPlaces(2).toNumber()) >
          (quantity * 0.01) + 1) {
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
        commit(returnTypes.ORDER_RETURN_SET_DRAFT_RA, {...draftRAInitial});
        commit(returnTypes.ORDER_RETURN_SET_DRAFT_REFUND, null);
        router.push({path: 'update'});
        throw Error(notify.ORDER_ERROR);
      }

      const request = D365RARepository.createReturnAuthorization(payload);

      // Create RA
      return request.then(response => {
        if (typeof response.data.data === 'undefined') {
          dispatch(orderTypes.WARNING, response.data.message);
        } else {
          const returnAuthorization = response.data.data;
          commit(returnTypes.ORDER_RETURN_SET_AUTHORIZATION,
              returnAuthorization);
          dispatch(orderTypes.SUCCESS,
              notify.ORDER_RETURN_AUTHORIZATION_CREATED);
          return returnAuthorization;
        }
      }).then(() => {
        // Update the order note in Shopify
        return ShopifyOrderRepository.updateOrder(region, orderId, {
          order: {
            note,
          },
        }).then(response => {
          dispatch(orderTypes.SUCCESS, notify.ORDER_UPDATED_NOTE);
          return response;
        });
      }).then(() => {
        return dispatch('releaseLock', {key: [region, orderId]});
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_STATE, null);
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
      }).catch((err) => {
        return Promise.reject(err);
      });
    },
    [returnTypes.ORDER_RETURN_SKIP_RA]({state, getters, dispatch, commit}) {
      const {region, note, order} = state;
      const {id: orderId} = order;
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);
      commit(returnTypes.ORDER_RETURN_SET_AUTHORIZATION, {
        id: 'DID NOT CREATE RA',
        region: '',
        invoice_id: '',
        return_status: '',
        warehouse_code: '',
        note: '',
        misc: '',
        gl_code: '',
        freight: '',
        currency: '',
        tax: '',
        tax_code: '',
        items: [],
      });
      ShopifyOrderRepository.updateOrder(region, orderId, {
        order: {
          note,
        },
      }).then(response => {
        dispatch(orderTypes.SUCCESS, notify.ORDER_UPDATED_NOTE);
        return response;
      }).then(() => {
        return dispatch('releaseLock', {key: [region, orderId]});
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_STATE, null);
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
      }).catch((err) => {
        return Promise.reject(err);
      });
    },
    [returnTypes.ORDER_RETURN_CREATE_DRAFT_RA]({
      getters,
      state,
      dispatch,
      commit,
    }) {
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);
      const discountIsApplied = getters[returnTypes.ORDER_RETURN_GET_DISCOUNT_IS_APPLIED];
      let action = null;

      if (discountIsApplied) {
        // If a discount is applied, we have to create a manual refund using the Shopify API
        action = dispatch(returnTypes.ORDER_REFUND_REFUND_CALC_DISCOUNT);
      } else {
        // If there is no discount, and we're refunding line items and/or shipping
        // then we can use the normal calculate refund API to do everything for us.
        action = dispatch(returnTypes.ORDER_REFUND_REFUND_CALC_LINE_ITEMS);
      }

      return action.then(refund => {
        const {transactions} = refund;

        transactions.forEach(transaction => {
          const {amount, maximum_refundable} = transaction;

          if (Number(amount) > Number(maximum_refundable) &&
              !transaction.gateway.match(/reach/i)) {
            const message = `${notify.ORDER_REFUND_TOO_LARGE}. Maximum Refundable: $${maximum_refundable}.`;
            throw new Error(message);
          }
        });
        commit(returnTypes.ORDER_RETURN_SET_DRAFT_REFUND, refund);
        dispatch(orderTypes.SUCCESS, notify.ORDER_REFUND_AVAILABLE);
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
      });
    },
    [returnTypes.ORDER_RETURN_CREATE_SHOPIFY_REFUND]({
      state,
      commit,
      dispatch,
    }) {
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);
      const {region, order, draftRefund, notifyRefund} = state;
      const {id: orderId} = order;

      // Create the refund in Shopify
      return ShopifyOrderRefundRepository.createRefund(
        region,
        orderId,
        {...draftRefund, ...{notify: notifyRefund}}
      ).then(response => {
        dispatch(orderTypes.SUCCESS, notify.ORDER_REFUNDED);
        return response;
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
      });
    },
    [returnTypes.ORDER_RETURN_DISCOUNT_PRICE_RULE_ADD]({
      state,
      commit,
      dispatch,
    }) {
      const {discount} = state;
      const {selected} = discount;
      let promise = null;

      switch (selected) {
        case 'Fixed':
          promise = dispatch(returnTypes.ORDER_RETURN_DISCOUNT_FETCH_FIXED,
              discount);
          break;
        case 'Percent':
          promise = dispatch(returnTypes.ORDER_RETURN_DISCOUNT_FETCH_PERCENT,
              discount);
          break;
        case 'Code':
          const discountCode = {...discount, strict: true};
          promise = dispatch(returnTypes.ORDER_RETURN_DISCOUNT_FETCH_CODE,
              discountCode);
          promise.catch(error => {
            commit(returnTypes.ORDER_RETURN_SET_DISCOUNT, {...discountInitial});
            throw error;
          });
          break;
        default:
          throw new Error(notify.ORDER_DISCOUNT_INVALID);
      }

      return promise.then(updatedDiscount => {
        return commit(returnTypes.ORDER_RETURN_SET_DISCOUNT, updatedDiscount);
      }).then(() => {
        const shipping = {...state.shipping, ...{price: 0}};
        return dispatch(returnTypes.ORDER_RETURN_FULL_REFUND, shipping);
      }).then(() => {
        return dispatch(orderTypes.SUCCESS, notify.DISCOUNT_ADDED);
      });
    },
    [returnTypes.ORDER_RETURN_DISCOUNT_FETCH_CODE]({state, commit}, discount) {
      const {region} = state;
      const {value, strict} = discount;
      const request = ShopifyPriceRuleRepository.discountCodeSearch(region,
          value, strict);
      return request.catch(error => {
        throw new Error(notify.CODE_NOT_FOUND);
      }).then(response => response.data).then(({price_rule}) => {
        return {
          ...discount,
          price_rule: {...price_rule, ...{code: value}},
        };
      });
    },
    [returnTypes.ORDER_RETURN_DISCOUNT_FETCH_FIXED](context, discount) {
      const {value} = discount;
      return new Promise(function(resolve) {
        resolve({
          ...discount,
          ...{
            price_rule: {
              allocation_limit: null,
              allocation_method: 'across',
              target_selection: 'all',
              target_type: 'line_item',
              value: new Decimal(value).times(-1).toNumber(),
              value_type: 'fixed_amount',
              title: '',
            },
          },
        });
      });
    },
    [returnTypes.ORDER_RETURN_DISCOUNT_FETCH_PERCENT](context, discount) {
      const {value} = discount;
      return new Promise(function(resolve) {
        resolve({
          ...discount,
          ...{
            price_rule: {
              allocation_limit: null,
              allocation_method: 'across',
              target_selection: 'all',
              target_type: 'line_item',
              value: new Decimal(value).times(-1).toNumber(),
              value_type: 'percentage',
              title: '',
            },
          },
        });
      });
    },
    [returnTypes.ORDER_RETURN_FULL_REFUND](
        {state, commit, dispatch},
        shipping) {
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);

      // Reset exchange on all products
      state.products.forEach((product, index) => {
        const updatedProduct = {index, product: {...product}};
        commit(returnTypes.ORDER_RETURN_SET_PRODUCT_UPDATE, updatedProduct);
      });

      // Update products to order quantity
      state.products.map((oldProduct, index) => {
        const returnItem = {
          ...oldProduct.returnItem, ...{
            quantity: oldProduct.lineItem.quantity,
          },
        };
        const product = {...oldProduct, ...{returnItem}};
        commit(returnTypes.ORDER_RETURN_SET_PRODUCT_UPDATE, {index, product});
      });

      // Update Shipping
      if (!shipping) {
        const {order: {shipping_lines: shippingLines}} = state;
        let price = 0;

        if (shippingLines.length > 0) {
          const shippingLine = _.cloneDeep(shippingLines[0]);
          const {discount_allocations: discountAllocations} = shippingLine;

          if (discountAllocations.length > 0) {
            price = Number(shippingLine.discounted_price);
          } else {
            price = Number(shippingLine.price);
          }
        }

        shipping = {...state.shipping, ...{price: price}};
      }
      commit(returnTypes.ORDER_RETURN_SET_SHIPPING, shipping);

      return dispatch(returnTypes.ORDER_RETURN_REFUND);
    },
    [returnTypes.ORDER_RETURN_INIT]({commit, state, dispatch}, {orderId}) {
      const {region} = state;

      dispatch('openLock', {key: [region, orderId]}).then(() => {
        return dispatch(returnTypes.ORDER_RETURN_FETCH, {orderId: orderId});
      }).catch(err => {
        dispatch('releaseLock', {key: [region, orderId]});

        throw err;
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_STATE, 'init');
      });
    },

    /**
     * Fetches an order and its corresponding D365 order, validates its state, and initializes
     * the return process in the store. Also sets up shipping, discount, and product data for the return.
     *
     * @param {Object} context - Vuex action context, destructured to {state, commit, dispatch}
     * @param {Object} payload - The payload object
     * @param {string|number} payload.orderId - The ID of the order to fetch
     * @returns {Promise<Object>} Resolves with the order object if successful
     */
    [returnTypes.ORDER_RETURN_FETCH]({state, commit, dispatch}, {orderId}) {
      commit(returnTypes.ORDER_RETURN_SET_ID, orderId);
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);
      dispatch(returnTypes.ORDER_RETURN_RESET);
      const {region} = state;

      return ShopifyOrderRepository.getOrder(region, orderId).then(response => {
        return response.data.order;
      }).then(order => {
        const {d365_id: d365Id} = order;
        const request = D365OrderRepository.getOrder(null, d365Id);

        // Make sure the order is in a state in D365 that allows to be edited
        return request.then(response => {
          if (!response) {
            throw new Error('Could not get order from D365.');
          }
          if (response.status >= 400) {
            throw response;
          }
          return response.data;
        }).then(d365Order => {
          const {
            state: d365OrderState,
          } = d365Order;
          commit(returnTypes.ORDER_RETURN_SET_D365, d365Order);
          let d365OrderStatus = d365Order.header.SalesOrderProcessingStatus;
          // if (d365OrderStatus !== D365_STATUS_INVOICED) {
          //   commit(returnTypes.ORDER_RETURN_SET_D365_ERROR, `${notify.D365_CANNOT_MODIFY_ORDER_IC} Order Status: ${d365OrderStatus}`);
          //   throw new Error(notify.D365_CANNOT_MODIFY_ORDER_IC);
          // }

          return order;
        }).catch(error => {
          commit(returnTypes.ORDER_RETURN_SET_STATE, null);
          throw error;
        });
      }).then(order => {
        const {shipping_lines: shippingLines} = order;
        const maxPrice = shippingLines.length > 0
            ? order.shipping_lines[0].discounted_price
            : 0;
        const shipping = {...state.shipping, ...{max_price: maxPrice}};
        const {discount_codes: discountCodes} = order;
        commit(returnTypes.ORDER_RETURN_SET_SHIPPING, shipping);
        commit(returnTypes.ORDER_RETURN_SET_ORDER, order);
        commit(returnTypes.ORDER_RETURN_SET_NOTE, order.note);
        // If the original order has a discount applied, then we need to grab it so
        // we can update the product prices accordingly for refunding
        if (discountCodes.length > 0) {
          const {total_discounts, total_line_items_price} = order;
          const percentDiscount = Number(total_discounts) / Number(total_line_items_price);
          commit('setOriginalDiscount', {
            percent: Math.ceil(percentDiscount * 10000) / 10000, // Percentage as a fraction eg 5.45% = .0545
            total_discounts,
            total_line_items_price,
          });
        }
        order.line_items.map(
            (lineItem) => dispatch(returnTypes.ORDER_RETURN_PRODUCT_ADD,
                {
                  region, lineItem,
                }));

        return order;
      }).catch((err) => {
        dispatch(orderTypes.ERROR, err.message);
        throw err;
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_STATE, 'loaded');
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
      });
    },

    /**
     * Vuex action to add a product to the return process.
     *
     * Fetches the product details from Shopify, extracts additional product details,
     * finds the corresponding D365 item, and prepares the return item object.
     * Commits the product and related data to the store.
     *
     * @param {Object} context - Vuex action context, destructured to {commit, state}
     * @param {Object} payload - The payload object
     * @param {string} payload.region - The region identifier
     * @param {Object} payload.lineItem - The line item from the order
     * @returns {Promise<Object>} Resolves with the product object from Shopify
     */
    [returnTypes.ORDER_RETURN_PRODUCT_ADD](
        {commit, state}, {region, lineItem}) {
      const {product_id: productId, sku} = lineItem;
      const request = ShopifyProductRepository.getProduct(region, productId);
      const productDetails = extractProductDetails(lineItem);
      const {d365Order: {lines: d365OrderItems}} = state;
      return request.then(response => {
        return response.data.product;
      }).then(product => {
        const d365Item = extractD365Item(lineItem, d365OrderItems, product);
        const variant = product.variants.find(variant => variant.sku = sku);
        const returnItem = {
          ...productDetails,
          sku: lineItem.sku,
          quantity: 0,
          inventory: lineItem.quantity,
          price: d365Item.SAXXNetUnitPrice,
          status: d365Item.SalesOrderLineStatus,
          line_number: lineItem.line_number,
          sales_channel_reference: lineItem.sales_channel_reference,
        };
        commit(returnTypes.ORDER_RETURN_SET_PRODUCT_ADD, {
          product,
          lineItem,
          returnItem,
          d365Item,
          variant,
          exchange: false,
        });
        return product;
      });
    },

    [returnTypes.ORDER_RETURN_REFUND]({state, commit, getters, dispatch}) {
      commit(returnTypes.ORDER_RETURN_SET_LOADING, true);

      const {region, order} = state;
      const draftOrder = getters[returnTypes.ORDER_RETURN_GET_DRAFT];
      const request = ShopifyOrderRepository.calculateTaxes(region, {
        draft_order: draftOrder,
        original_order: order,
      });

      return request.then(response => {
        return response.data;
      }).then(draftOrder => {
        commit(returnTypes.ORDER_RETURN_SET_DRAFT_RA, draftOrder);
        return draftOrder;
      }).then(draftOrder => {
        const {tax_lines} = draftOrder;

        if (tax_lines.length < 1) {
          dispatch(orderTypes.WARNING, notify.TAX_LINES_ARE_EMPTY);
        }

        return draftOrder;
      }).finally(() => {
        commit(returnTypes.ORDER_RETURN_SET_LOADING, false);
      });
    },
    [returnTypes.ORDER_REFUND_REFUND_CALC_LINE_ITEMS]({
      state,
      commit,
      getters,
    }) {
      const {products, shipping, order, region} = state;
      const {id: orderId, currency} = order;
      const {price: shippingPrice} = shipping;

      const reducer = (refundLineItems, product) => {
        const {returnItem, lineItem, exchange} = product;
        const returnItemQuantity = Number(returnItem.quantity);
        const lineItemId = lineItem.id;

        // If it's an exchange, we don't factor in the price
        if (exchange) {
          return refundLineItems;
        }

        if (returnItemQuantity > 0) {
          refundLineItems.push({
            'line_item_id': lineItemId,
            'quantity': returnItemQuantity,
            'restock_type': 'no_restock',
            'overrideprice': lineItem.pre_tax_price / lineItem.quantity || null,
            'overridetax': returnItem.price * lineItem.tax_lines.reduce(
                (prev, curr) => prev +
                    (curr.price > 0 ? parseFloat(curr.rate) : 0), 0),
          });
        }

        return refundLineItems;
      };
      const refundLineItems = products.reduce(reducer, []);
      const refund = {
        'currency': currency,
        'shipping': {
          'amount': shippingPrice,
        },
        'refund_line_items': refundLineItems,
        'max_tax': order.total_tax,
      };
      const draftOrder = getters[returnTypes.ORDER_RETURN_GET_REFUND_DRAFT];
      return ShopifyOrderRepository.calculateTaxes(region, {
        draft_order: draftOrder,
        original_order: order,
      }).then(response => response.data).then(draftOrder => {
        commit(returnTypes.ORDER_RETURN_SET_DRAFT_ORDER, draftOrder);
        const request = ShopifyOrderRefundRepository.calculateRefund(region,
            orderId, refund, draftOrder);
        return request.then(response => {
          return response.data.refund;
        });
      });
    },
    [returnTypes.ORDER_REFUND_REFUND_CALC_DISCOUNT]({state, commit, getters}) {
      const {region, order} = state;
      const draftOrder = getters[returnTypes.ORDER_RETURN_GET_REFUND_DRAFT];
      const {id: orderId} = order;

      // Create the draft order that will be used for calculating the refund
      // in Shopify... this version is different than the draft RA since
      // Shopify does the diff'ing for you and returns that difference as the
      // amount to refund, where the draft RA is the amount to refund.
      return ShopifyOrderRepository.calculateTaxes(region, {
        draft_order: draftOrder,
        original_order: order,
      }).then(response => {
        const draftOrder = response.data;

        commit(returnTypes.ORDER_RETURN_SET_DRAFT_ORDER, draftOrder);

        return draftOrder;
      }).then(draftOrder => {
        // Send off old order and new order to Shopify to get the refund calculation
        return ShopifyOrderRefundRepository.calculateRefundDifference(region,
            orderId, {
              original_order: order,
              updated_order: draftOrder,
            }).then(response => {
          return response.data.refund;
        });
      });
    },
    [returnTypes.ORDER_RETURN_RESET]({commit}) {
      commit(returnTypes.ORDER_RETURN_SET_STATE, null);
      commit(returnTypes.ORDER_RETURN_SET_DRAFT_RA, {...draftRAInitial});
      commit(returnTypes.ORDER_RETURN_SET_DRAFT_REFUND, null);
      commit(returnTypes.ORDER_RETURN_SET_DRAFT_ORDER, null);
      commit(returnTypes.ORDER_RETURN_SET_DISCOUNT, {...discountInitial});
      commit(returnTypes.ORDER_RETURN_SET_NOTE, '');
      commit(returnTypes.ORDER_RETURN_SET_ORDER, null);
      commit(returnTypes.ORDER_RETURN_SET_PRODUCTS, []);
      commit(returnTypes.ORDER_RETURN_SET_REASON_CODE, '');
      commit(returnTypes.ORDER_RETURN_SET_REMARKS, '');
      commit(returnTypes.ORDER_RETURN_SET_SHIPPING, {...shippingInitial});
      commit(returnTypes.ORDER_RETURN_SET_WAREHOUSE, false);
      commit(returnTypes.ORDER_RETURN_SET_LOGON_ERROR, '');
      commit(returnTypes.ORDER_RETURN_SET_LOGON_ERROR, null);
      commit(returnTypes.ORDER_RETURN_SET_D365_ERROR, '');
      commit(returnTypes.ORDER_RETURN_SET_D365_ERROR, null);
    },
  },
  getters: {
    [returnTypes.ORDER_RETURN_IS_LOADED](state) {
      if (state.loading) {
        return false;
      }

      if (!state.order) {
        return false;
      }

      if (!state.order.id) {
        return false;
      }

      return true;
    },
    [returnTypes.ORDER_RETURN_GET_AUTHORIZATION](state, getters) {
      const discountIsApplied = getters[returnTypes.ORDER_RETURN_GET_DISCOUNT_IS_APPLIED];
      const {price_rule} = state.discount;
      const coupon = discountIsApplied && price_rule.code
          ? price_rule.code
          : '';
      const tax = Number(state.draftRA.total_tax);
      const freight = Number(state.shipping.price);
      let discount = 0;

      // We only add discount field if a discount is applied
      if (discountIsApplied) {
        discount = Number(state.draftRA.subtotal_price);
      }

      return {
        'order_id': state.order ? state.order.id : null,
        'region': state.region,
        'remarks': state.raRemarks === 'CF'
            ? state.textRemarks
            : state.raRemarks,
        'return_status': state.reasonCode,
        'return_reason': state.reasonCodeDetail,
        'return_to_warehouse': state.warehouse, //
        'tax': tax,
        'freight': freight,
        'coupon': coupon,
        'discount': discount, //
        'items': getters[returnTypes.ORDER_RETURN_GET_AUTHORIZATION_ITEMS],
        'refund': state.draftRefund,
      };
    },
    [returnTypes.ORDER_RETURN_GET_AUTHORIZATION_ITEMS](state) {
      const reducer = (items, product) => {
        if (product.returnItem.quantity > 0) {
          const returnItem = {...product.returnItem};
          returnItem['unit_price'] = product.exchange ? 0 : Number(
              returnItem.price);
          items.push(returnItem);
        }
        return items;
      };
      return state.products.reduce(reducer, []);
    },
    [returnTypes.ORDER_RETURN_GET_DISCOUNT_IS_APPLIED](state) {
      return !!state.discount.price_rule;
    },
    [returnTypes.ORDER_RETURN_GET_DRAFT](state, getters) {
      const discountIsApplied = getters[returnTypes.ORDER_RETURN_GET_DISCOUNT_IS_APPLIED];
      let appliedDiscount = null;

      if (discountIsApplied) {
        const {price_rule: priceRule} = state.discount;
        const {value_type, value} = priceRule;
        const total = state.products.reduce(totalLineItemsPriceReducer, 0);
        let amount = 0;
        let appliedDiscountValue = 0;
        let discountValue = Math.abs(value);

        switch (value_type) {
          case 'fixed_amount':
            appliedDiscountValue = percentOf(total - discountValue, 1);
            amount = percentOf(total - discountValue, 1);
            break;
          case 'percentage':
            appliedDiscountValue = 100 - discountValue;
            amount = percentOf(total, appliedDiscountValue / 100);
            break;
        }

        appliedDiscount = {
          'title': 'RAA Discount',
          'description': 'RAA Discount',
          'value': appliedDiscountValue,
          'value_type': value_type,
          'amount': amount,
        };
      }

      return {
        'applied_discount': appliedDiscount,
        'billing_address': state.order ? state.order.billing_address : {},
        'line_items': getters[returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS],
        'shipping_address': state.order ? state.order.shipping_address : {},
        'shipping_line': state.shipping,
        'order_id': state.order ? state.order.id : '',
      };
    },
    [returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS](state) {
      const reducer = (items, product) => {
        if (product.returnItem.quantity > 0) {
          const {variant, lineItem, d365Item, returnItem, exchange} = product;
          const {variant_id: variantId, price: lineItemPrice} = lineItem;
          const {SAXXNetUnitPrice: d365ItemPrice} = d365Item;
          const discountDifference = Number(lineItemPrice) -
              Number(d365ItemPrice);
          let appliedDiscount = null;
          const taxable = sumTaxLines(lineItem['tax_lines']) > 0;

          // If it's an exchange, we don't factor in the price
          if (exchange) {
            return items;
          }

          if (Math.abs(discountDifference) > 0) {
            appliedDiscount = {
              'description': 'Refund Discount',
              'value_type': 'fixed_amount',
              'value': discountDifference,
              'amount': discountDifference * returnItem.quantity,
              'title': 'Refund Discount',
            };
          }

          // For the situation where the price has changed since purchase,
          // We will use whatever was in LogOn for the order instead of having Shopify
          // fetching for us through the variant ID.
          const lineitemPrice = new Decimal(lineItem.price);

          if (!lineitemPrice.equals(variant.price) ||
              !lineitemPrice.equals(returnItem.price)) {
            items.push({
              ...product.returnItem, ...{
                title: lineItem.title,
                price: lineItem.price,
                product_id: lineItem.product_id,
                variant_id: variantId,
                custom: true,
                quantity: returnItem.quantity,
                applied_discount: appliedDiscount,
                taxable: taxable,
              },
            });
          } else {
            items.push({
              ...product.returnItem, ...{
                variant_id: variantId,
                quantity: returnItem.quantity,
                product_id: lineItem.product_id,
                applied_discount: appliedDiscount,
                taxable: taxable,
                title: lineItem.title,
              },
            });
          }
        }

        return items;
      };
      return _.cloneDeep(state.products).reduce(reducer, []);
    },
    [returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS_COUNT](state, getters) {
      const lineItems = getters[returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS];
      const reducer = (count, lineItem) => count + lineItem.quantity;
      return lineItems.reduce(reducer, 0);
    },
    [returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS_DISCOUNT](state, getters) {
      const lineItemCount = getters[returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS_COUNT];
      const {price_rule} = state.discount;
      let lineItemDiscount = 0;

      if (price_rule && lineItemCount > 0) {
        const {value, value_type} = price_rule;
        const {subtotal_price} = state.order;

        switch (value_type) {
          case 'fixed_amount':
            lineItemDiscount = Number(value) / lineItemCount;
            break;
          case 'percentage':
            const percent = Number(value) / 100;
            const totalDiscount = Number(subtotal_price) * percent;
            lineItemDiscount = totalDiscount / lineItemCount;
            break;
        }
      }

      return lineItemDiscount;
    },
    [returnTypes.ORDER_RETURN_GET_DISCOUNT](state, getters) {
      return state.discount.price_rule || null;
    },
    [returnTypes.ORDER_RETURN_GET_REFUND_DRAFT](state, getters) {
      const discountIsApplied = getters[returnTypes.ORDER_RETURN_GET_DISCOUNT_IS_APPLIED];
      let appliedDiscount = null;

      // There are two situations for refunds:
      // 1. Refunding line items
      // 2. Applying discounts
      // If we're refunding line items we will let Shopify know when calculating
      // the refund, otherwise we apply the discount to the order.
      if (discountIsApplied) {
        const {
          price_rule: priceRule,
        } = state.discount;
        const {
          title, value_type, value,
        } = priceRule;
        const total = state.products.reduce(totalLineItemsPriceReducer, 0);
        let amount = 0;
        let appliedDiscountValue = 0;
        let discountValue = Math.abs(Number(value));

        switch (value_type) {
          case 'fixed_amount':
            appliedDiscountValue = discountValue;
            amount = discountValue;
            break;
          case 'percentage':
            appliedDiscountValue = discountValue;
            amount = percentOf(total, appliedDiscountValue / 100);
            break;
        }

        appliedDiscount = {
          'title': 'RAA Discount',
          'description': title,
          'value': appliedDiscountValue,
          'value_type': value_type,
          'amount': amount,
        };
      }

      return {
        'applied_discount': appliedDiscount,
        'shipping_address': state.order ? state.order.shipping_address : {},
        'billing_address': state.order ? state.order.billing_address : {},
        'line_items': getters[returnTypes.ORDER_RETURN_GET_REFUND_DRAFT_LINE_ITEMS],
        'shipping_line': getters[returnTypes.ORDER_RETURN_GET_REFUND_DRAFT_SHIPPING_LINE],
        'order_id': state.order.id,
      };
    },
    [returnTypes.ORDER_RETURN_GET_REFUND_DRAFT_LINE_ITEMS](state, getters) {
      const discountIsApplied = getters[returnTypes.ORDER_RETURN_GET_DISCOUNT_IS_APPLIED];
      const reducer = (items, product) => {
        const {lineItem, d365Item, returnItem, variant, exchange} = product;
        const {
          variant_id: variantId,
          price: lineItemPrice,
          quantity: lineItemQuantity,
        } = lineItem;
        const {SalesPrice: d365ItemPrice} = d365Item;
        const {quantity: returnItemQuantity} = returnItem;
        let appliedDiscount = null;
        const taxable = sumTaxLines(lineItem['tax_lines']) > 0;
        const discountDifference = Number(lineItemPrice) -
            Number(d365ItemPrice);

        if (exchange) {
          return items;
        }

        // If the original order has a discount applied, then we have to re-apply the discount here
        if (Math.abs(discountDifference) > 0) {
          appliedDiscount = {
            'description': 'Refund Discount',
            'value_type': 'fixed_amount',
            'value': discountDifference,
            'amount': discountDifference * lineItemQuantity,
            'title': 'Refund Discount',
          };
        }

        if (discountIsApplied) {
          // If a discount is applied then the quantity is the full quantity
          // since the discount will be applied to the order overall.
          if (variant.price !== lineItem.price) {
            items.push({
              ...product.returnItem, ...{
                title: lineItem.title,
                price: lineItem.price,
                product_id: lineItem.product_id,
                variant_id: variantId,
                custom: true,
                quantity: returnItem.quantity,
                applied_discount: appliedDiscount,
                taxable: taxable,
              },
            });
          } else {
            items.push({
              sku: lineItem.sku,
              variant_id: variantId,
              quantity: lineItemQuantity,
              product_id: lineItem.product_id,
              applied_discount: appliedDiscount,
              taxable: taxable,
              title: lineItem.title,
            });
          }
        } else if (returnItemQuantity > 0) {
          // Otherwise we only add the item is it being returned.
          if (variant.price !== lineItem.price) {
            items.push({
              ...product.returnItem, ...{
                title: lineItem.title,
                price: lineItem.price,
                product_id: lineItem.product_id,
                variant_id: variantId,
                custom: true,
                quantity: returnItem.quantity,
                applied_discount: appliedDiscount,
                taxable: taxable,
              },
            });
          } else {
            items.push({
              sku: lineItem.sku,
              variant_id: variantId,
              quantity: lineItemQuantity,
              product_id: lineItem.product_id,
              applied_discount: appliedDiscount,
              taxable: taxable,
              title: lineItem.title,
            });
          }
        }

        return items;
      };
      return _.cloneDeep(state.products).reduce(reducer, []);
    },
    [returnTypes.ORDER_RETURN_GET_REFUND_DRAFT_SHIPPING_LINE]({
      order,
      shipping,
    }) {
      if (!order) {
        return null;
      }
      const {shipping_lines: shippingLines} = order;
      const oldShippingPrice = shippingLines.length > 0 ? Number(
          shippingLines[0].discounted_price || shippingLines[0].price) : 0;
      const newShippingPrice = Number(shipping.price);

      return {
        ...shipping,
        price: Math.min(oldShippingPrice - newShippingPrice, shipping.max_price),
      };
    },
    [returnTypes.ORDER_RETURN_GET_ROUNDING_ERROR](state, getters) {
      const roundingErrorPerItem = 0.01;
      const reducer = getters[returnTypes.ORDER_RETURN_GET_DRAFT_LINE_ITEMS_COUNT];
      return reducer * roundingErrorPerItem;
    },
  },
};
